import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { CSPUrlsMap } from '../../../routes/CSPUrlsMap';
import useVerify from '../../verification/useVerify';
import { FORM_KEYS } from '../../../constants/formKeys';
import { EMAIL_REGEX } from '../../../constants/constants';
import { SLICES } from '../../../constants/slices';
import { useGetContactDetailsQuery, useUpdateContactDetailsMutation } from '../../../data/policy';
import { getAllPolicyHolders } from '../../../helpers/policyHelpers';
import { legacyPolicyNumberHandler } from '../../../helpers/paymentHelper';

const useUpdateEmail = () => {
  const { logout } = useAuth0();
  const [updateContactDetails] = useUpdateContactDetailsMutation();
  const navigate = useNavigate();
  const { watch } = useFormContext();
  const [isUpdateEmailStep, setIsUpdateEmailStep] = React.useState(true);
  const newEmailValue = watch(FORM_KEYS.NEW_EMAIL);
  const { tokenClaims, authDetails } = useSelector((state) => state?.[SLICES.AUTHENTICATION]);
  const { petDetailsList } = useSelector((state) => state?.[SLICES.PET_DETAILS]);
  const policyHolders = getAllPolicyHolders({ petDetails: petDetailsList });
  const currentPolicyHolder = policyHolders.length >= 1 && policyHolders[0];
  const { data: contactDetails, isLoading: contactDetailsLoading } = useGetContactDetailsQuery({
    policyNumber: legacyPolicyNumberHandler(currentPolicyHolder?.policyNumber),
    auth0Token: authDetails?.auth0AccessToken
  });
  const { getVerifyCode, isCodeSent, isCodeVerified, confirmUserEnrollment, errorMessage, timeLeft } = useVerify({
    verifyType: 'email',
    verifyTypeInput: newEmailValue,
    verifyCode: watch(FORM_KEYS.VERIFY_EMAIL_CODE),
    skipRedirection: true,
    isEnabled: false
  });

  const steps = [
    { isCompleted: !isUpdateEmailStep, isActive: isUpdateEmailStep, label: 'UPDATE EMAIL' },
    { isActive: !isUpdateEmailStep, label: 'VERIFY EMAIL' }
  ];

  useEffect(() => {
    const updateNewEmail = async () => {
      const updateEmailPayload = {
        policyNumber: contactDetails?.policyNumber,
        phoneMobile: contactDetails?.phoneMobile,
        phoneHome: '',
        phoneWork: '',
        email: newEmailValue
      };
      try {
        await updateContactDetails(updateEmailPayload);
      } catch (error) {
        throw error('Error updating new email: ', error);
      }
    };
    if (isCodeVerified) {
      updateNewEmail();
    }
  }, [isCodeVerified, newEmailValue, tokenClaims?.phoneNumber, updateContactDetails, petDetailsList, contactDetails]);

  useEffect(() => {
    if (!errorMessage && isCodeSent) {
      setIsUpdateEmailStep(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorMessage, isCodeSent]);

  const onUpdateClick = useCallback(() => {
    if (!isCodeSent) {
      getVerifyCode();
      if (errorMessage) {
        setIsUpdateEmailStep(true);
      }
    }
  }, [errorMessage, getVerifyCode, isCodeSent]);

  const onCancelClick = useCallback(() => {
    navigate(CSPUrlsMap.accountDetails.url);
  }, [navigate]);

  const validation = {
    pattern: {
      value: EMAIL_REGEX,
      message: 'Email address is invalid.'
    }
  };

  const loginUrl = `${window.location.origin}${CSPUrlsMap.login.url}`;
  const onSuccessModalContinueClicked = () => {
    sessionStorage.clear();
    localStorage.clear();
    logout({
      logoutParams: { returnTo: loginUrl }
    });
  };
  return {
    steps,
    isUpdateEmailStep,
    onUpdateClick,
    onCancelClick,
    validation,
    newEmailValue,
    contactDetailsLoading,
    errorMessage,
    isCodeVerified,
    getVerifyCode,
    isCodeSent,
    confirmUserEnrollment,
    timeLeft,
    onSuccessModalContinueClicked
  };
};

export default useUpdateEmail;
